// src/pages/Auth/SelectRole.jsx - FINAL COMPLETE VERSION
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  ShoppingCart,
  Store,
  CheckCircle,
  Person,
  Business,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { authAPI } from '../../services/api';
import toast from 'react-hot-toast';

const SelectRole = () => {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // No key needed — token is already in localStorage (saved by GoogleCallback
    // or via ProtectedRoute redirect for existing users without user_type).
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Session expired. Please login again.');
      setLoading(false);
      return;
    }
    setLoading(false);
  }, [location]);

  const handleRoleSelect = async (role) => {
    try {
      setProcessing(true);
      setError('');

      // Use JWT-based update — no cross-worker temp store needed
      const response = await authAPI.updateUserType({ user_type: role });

      if (response.data.success) {
        const { user, access_token } = response.data;

        localStorage.setItem('token', access_token || localStorage.getItem('token'));
        localStorage.setItem('user', JSON.stringify(user));

        toast.success(
          `Welcome ${user.company_name || user.email}! Account set up as ${role === 'seller' ? 'Seller' : 'Buyer'}!`,
          { duration: 4000 }
        );

        // ✅ Redirect to KYC verification if not yet verified
        // New users need to complete KYC before they can trade
        setTimeout(() => {
          if (!user.verified) {
            navigate(role === 'seller' ? '/seller/verification' : '/buyer/verification');
          } else {
            navigate(role === 'seller' ? '/seller/dashboard' : '/buyer/dashboard');
          }
        }, 2000);
      } else {
        throw new Error(response.data.message || 'Failed to update role');
      }
    } catch (err) {
      console.error('Role selection error:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to set account type. Please try again.';
      setError(errorMsg);
      toast.error(errorMsg);
      setProcessing(false);
    }
  };

  const handleCancel = () => {
    localStorage.clear();
    navigate('/login');
  };

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ 
          mt: 8, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh'
        }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 3 }}>
            Loading account setup...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ 
        mt: { xs: 4, md: 8 }, 
        mb: 6,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        
        {/* Header */}
        <Paper sx={{ 
          p: 4, 
          width: '100%',
          maxWidth: 800,
          borderRadius: 3, 
          boxShadow: 3,
          mb: 4,
          textAlign: 'center',
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        }}>
          <Typography variant="h3" component="h1" gutterBottom fontWeight="bold" color="primary">
            Welcome to Fruit Export Platform! 🍎
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            Complete your account setup
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Choose how you want to use our platform. You can always change this later in settings.
          </Typography>
          
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mt: 3,
                textAlign: 'left',
                borderRadius: 2
              }}
            >
              {error}
            </Alert>
          )}
        </Paper>

        {/* Role Selection Cards */}
        <Grid container spacing={3} sx={{ maxWidth: 800 }}>
          {/* Buyer Card */}
          <Grid item xs={12} md={6}>
            <Card
              elevation={selectedRole === 'buyer' ? 8 : 2}
              sx={{
                height: '100%',
                cursor: processing ? 'default' : 'pointer',
                border: 3,
                borderColor: selectedRole === 'buyer' ? 'primary.main' : 'transparent',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': processing ? {} : {
                  transform: 'translateY(-8px)',
                  boxShadow: 8,
                  borderColor: selectedRole === 'buyer' ? 'primary.dark' : 'rgba(46, 125, 50, 0.3)',
                },
              }}
              onClick={() => !processing && setSelectedRole('buyer')}
            >
              {selectedRole === 'buyer' && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    color: 'primary.main',
                  }}
                >
                  <CheckCircle fontSize="large" />
                </Box>
              )}
              
              <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  textAlign: 'center',
                  mb: 3
                }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: selectedRole === 'buyer' ? 'primary.light' : 'grey.100',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <ShoppingCart sx={{ fontSize: 40, color: selectedRole === 'buyer' ? 'primary.main' : 'grey.600' }} />
                  </Box>
                  
                  <Typography variant="h4" component="h2" gutterBottom fontWeight="bold">
                    I want to Buy Fruits
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
                    Import fresh fruits from global suppliers
                  </Typography>
                </Box>

                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    <Person sx={{ color: 'primary.main', mr: 2, mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">For Individual Buyers</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Purchase fruits for personal use or small businesses
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    <Business sx={{ color: 'primary.main', mr: 2, mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">For Companies & Importers</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Bulk purchasing for supermarkets, restaurants, or distributors
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Perfect for: Importers, Retailers, Restaurants, Food Processors
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Seller Card */}
          <Grid item xs={12} md={6}>
            <Card
              elevation={selectedRole === 'seller' ? 8 : 2}
              sx={{
                height: '100%',
                cursor: processing ? 'default' : 'pointer',
                border: 3,
                borderColor: selectedRole === 'seller' ? 'secondary.main' : 'transparent',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': processing ? {} : {
                  transform: 'translateY(-8px)',
                  boxShadow: 8,
                  borderColor: selectedRole === 'seller' ? 'secondary.dark' : 'rgba(255, 152, 0, 0.3)',
                },
              }}
              onClick={() => !processing && setSelectedRole('seller')}
            >
              {selectedRole === 'seller' && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    color: 'secondary.main',
                  }}
                >
                  <CheckCircle fontSize="large" />
                </Box>
              )}
              
              <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  textAlign: 'center',
                  mb: 3
                }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      bgcolor: selectedRole === 'seller' ? 'secondary.light' : 'grey.100',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <Store sx={{ fontSize: 40, color: selectedRole === 'seller' ? 'secondary.main' : 'grey.600' }} />
                  </Box>
                  
                  <Typography variant="h4" component="h2" gutterBottom fontWeight="bold">
                    I want to Sell Fruits
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
                    Export fresh fruits to international buyers
                  </Typography>
                </Box>

                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    <Business sx={{ color: 'secondary.main', mr: 2, mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">For Farmers & Growers</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Sell directly to international buyers and get better prices
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    <Person sx={{ color: 'secondary.main', mr: 2, mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">For Export Companies</Typography>
                      <Typography variant="body2" color="text.secondary">
                        List your fruit inventory and reach global customers
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Perfect for: Farmers, Growers, Exporters, Wholesalers, Distributors
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Paper sx={{ 
          p: 4, 
          width: '100%',
          maxWidth: 800,
          borderRadius: 3, 
          boxShadow: 3,
          mt: 4,
          textAlign: 'center'
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <Button
              variant="contained"
              size="large"
              disabled={!selectedRole || processing}
              onClick={() => handleRoleSelect(selectedRole)}
              sx={{ 
                px: 8, 
                py: 1.8,
                minWidth: 240,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                borderRadius: 2,
                boxShadow: 4,
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {processing ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 2, color: 'white' }} />
                  Setting up your account...
                </>
              ) : (
                `Continue as ${selectedRole === 'seller' ? 'Seller' : 'Buyer'}`
              )}
            </Button>
            
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                You can change your account type later in settings
              </Typography>
              
              <Button
                variant="outlined"
                color="error"
                onClick={handleCancel}
                disabled={processing}
                sx={{ 
                  borderRadius: 2,
                  px: 4,
                  '&:hover': {
                    backgroundColor: 'error.light',
                    color: 'white',
                  }
                }}
              >
                Cancel and go back to login
              </Button>
            </Box>
            
            {/* Progress indicator */}
            {processing && (
              <Box sx={{ width: '100%', maxWidth: 400, mt: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Setting up your account...
                </Typography>
                <Box sx={{ 
                  width: '100%', 
                  height: 4, 
                  backgroundColor: 'grey.200',
                  borderRadius: 2,
                  overflow: 'hidden'
                }}>
                  <Box 
                    sx={{ 
                      width: '60%', 
                      height: '100%', 
                      backgroundColor: 'primary.main',
                      borderRadius: 2,
                      animation: 'pulse 2s infinite'
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Features Comparison */}
        <Paper sx={{ 
          p: 4, 
          width: '100%',
          maxWidth: 800,
          borderRadius: 3, 
          boxShadow: 2,
          mt: 4,
          backgroundColor: 'grey.50'
        }}>
          <Typography variant="h6" gutterBottom fontWeight="bold" textAlign="center" color="text.primary">
            Platform Features Comparison
          </Typography>
          
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
                Buyer Benefits:
              </Typography>
              <ul style={{ paddingLeft: 20, margin: 0 }}>
                <li><Typography variant="body2">Access to verified global fruit suppliers</Typography></li>
                <li><Typography variant="body2">Real-time price comparison</Typography></li>
                <li><Typography variant="body2">Secure payment processing</Typography></li>
                <li><Typography variant="body2">Shipment tracking</Typography></li>
                <li><Typography variant="body2">Quality assurance</Typography></li>
              </ul>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight="bold" color="secondary" gutterBottom>
                Seller Benefits:
              </Typography>
              <ul style={{ paddingLeft: 20, margin: 0 }}>
                <li><Typography variant="body2">Global market access</Typography></li>
                <li><Typography variant="body2">Direct buyer communication</Typography></li>
                <li><Typography variant="body2">Inventory management tools</Typography></li>
                <li><Typography variant="body2">Analytics dashboard</Typography></li>
                <li><Typography variant="body2">Secure transaction system</Typography></li>
              </ul>
            </Grid>
          </Grid>
        </Paper>
      </Box>
      
      {/* Add CSS animation */}
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
    </Container>
  );
};

export default SelectRole;